import { api } from "../api/client";
import type { PathCandidate } from "./fileRefDetect";

export interface ResolvedRef {
  path: string;
  line?: number;
  restricted?: boolean;
  restrictedReason?: string | null;
}
export interface ResolveCtx {
  projectId: string | null;
  rootPath: string | null;
}

interface CacheEntry {
  /** Resolved project-relative path, or null for a known miss. */
  value: string | null;
  restricted: boolean;
  restrictedReason: string | null;
  /** Expiry for negative entries; positives never expire (Infinity). */
  expires: number;
}

const NEGATIVE_TTL_MS = 30_000;
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<CacheEntry>>();

function keyFor(ctx: ResolveCtx, pathNoSuffix: string): string {
  return `${ctx.projectId ?? ""}|${ctx.rootPath ?? ""}|${pathNoSuffix}`;
}

/** Test seam: drop all cached + in-flight resolutions. */
export function clearFileRefCache(): void {
  cache.clear();
  inflight.clear();
}

export async function resolveFileRef(
  candidate: PathCandidate,
  ctx: ResolveCtx
): Promise<ResolvedRef | null> {
  const key = keyFor(ctx, candidate.path);
  const now = Date.now();

  const cached = cache.get(key);
  if (cached && cached.expires > now) {
    return entryToRef(cached, candidate.line);
  }

  let pending = inflight.get(key);
  if (!pending) {
    pending = (async (): Promise<CacheEntry> => {
      try {
        const res = await api.resolveFile({
          projectId: ctx.projectId,
          rootPath: ctx.rootPath,
          path: candidate.path,
        });
        const value = res.resolved ?? null;
        return {
          value,
          restricted: Boolean(res.restricted),
          restrictedReason: res.restrictedReason ?? null,
          expires: value ? Infinity : Date.now() + NEGATIVE_TTL_MS,
        };
      } catch {
        // Fail-open: a transient failure is a short-lived miss, not an error.
        return {
          value: null,
          restricted: false,
          restrictedReason: null,
          expires: Date.now() + NEGATIVE_TTL_MS,
        };
      }
    })();
    inflight.set(key, pending);
  }

  const entry = await pending;
  inflight.delete(key);
  cache.set(key, entry);
  return entryToRef(entry, candidate.line);
}

function entryToRef(entry: CacheEntry, line?: number): ResolvedRef | null {
  if (!entry.value) return null;
  return {
    path: entry.value,
    line,
    restricted: entry.restricted,
    restrictedReason: entry.restrictedReason,
  };
}
